
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import RelevanceInput from "@/components/RelevanceInput";
import BusinessInfoForm from "@/components/BusinessInfoForm";
import { useGoogleAuth } from "@/contexts/GoogleAuthContext";
import { supabase } from "@/integrations/supabase/client";
import APIKeyDebug from "@/components/debug/APIKeyDebug";

const AIContext = () => {
  const [relevanceContext, setRelevanceContext] = useState("");
  const [businessInfo, setBusinessInfo] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { isAuthenticated, supabaseSession } = useGoogleAuth();
  
  // Load saved context data on initial mount
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        // Try to load from Supabase first if user is authenticated
        if (isAuthenticated && supabaseSession?.user?.id) {
          const { data } = await supabase
            .from("ai_context")
            .select("relevance_context, business_info")
            .eq("user_id", supabaseSession.user.id)
            .maybeSingle();
            
          if (data) {
            if (data.relevance_context) {
              setRelevanceContext(data.relevance_context);
            }
            
            if (data.business_info) {
              setBusinessInfo(data.business_info);
            }
            console.log("Loaded context data from Supabase");
          }
        }
        
        // Fall back to localStorage if needed
        if (!relevanceContext) {
          const savedRelevanceContext = localStorage.getItem("relevance_context");
          if (savedRelevanceContext) {
            setRelevanceContext(savedRelevanceContext);
          }
        }
        
        if (!businessInfo) {
          const savedBusinessInfo = localStorage.getItem("business_info");
          if (savedBusinessInfo) {
            setBusinessInfo(savedBusinessInfo);
          }
        }
        
        console.log("AI Context: Loaded saved context data");
      } catch (error) {
        console.error("Error loading saved context data:", error);
      }
    };
    
    loadSavedData();
  }, [isAuthenticated, supabaseSession]);
  
  // Simple handler to save relevance context
  const handleRelevanceSubmit = async (context: string) => {
    try {
      setIsAnalyzing(true);
      
      // Save to localStorage
      localStorage.setItem("relevance_context", context);
      
      // If user is authenticated, also save to Supabase
      if (isAuthenticated && supabaseSession?.user?.id) {
        const userId = supabaseSession.user.id;
        
        // Check if record exists
        const { data: existingRecord } = await supabase
          .from("ai_context")
          .select("id")
          .eq("user_id", userId)
          .maybeSingle();
          
        if (existingRecord) {
          // Update existing record
          await supabase
            .from("ai_context")
            .update({
              relevance_context: context,
              updated_at: new Date().toISOString()
            })
            .eq("id", existingRecord.id);
        } else {
          // Insert new record
          await supabase
            .from("ai_context")
            .insert([{
              user_id: userId,
              relevance_context: context
            }]);
        }
      }
      
      toast.success("Relevance criteria saved successfully");
    } catch (error) {
      console.error("Error saving relevance context:", error);
      toast.error("Failed to save relevance criteria");
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  // Handler for the business info changes
  const handleBusinessInfoChange = (info: string) => {
    // Update local state
    setBusinessInfo(info);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">AI Context Configuration</h1>
      
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Configure AI Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
            <div>
              <h2 className="text-lg font-medium mb-4">Relevance Criteria</h2>
              <RelevanceInput 
                onSubmit={handleRelevanceSubmit} 
                isLoading={isAnalyzing} 
                relevanceContext={relevanceContext}
                setRelevanceContext={setRelevanceContext}
              />
            </div>
            
            <div>
              <h2 className="text-lg font-medium mb-4">Business Information</h2>
              <BusinessInfoForm 
                businessInfo={businessInfo}
                onBusinessInfoChange={handleBusinessInfoChange}
              />
            </div>
            
            {/* Add API Key Debug Panel */}
            <div>
              <APIKeyDebug />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIContext;
